import { useMinimumPercentageOffToDisplay } from '@shared/hooks/useMinimumPercentageOffToDisplay';
import { getPercentageOff } from '@shared/utils/accommodationPrice';
import { FormatCurrencyHandler, useCurrencyFormatter } from '@shared/utils/currency';
import { useMemo } from 'react';
import { HotelRoomBlockPricingValues, RawJoyHotelPricingInfo, RoomBlockWithPricePerNight, StandardHotelPricingValues } from '../AccommodationsConsole.types';

const getCurrencyFormatter = (formatter: FormatCurrencyHandler) => (pricingInfo: { floatingPointDecimalString: string; currency: string | undefined }) => {
  return formatter({
    priceFloatingPointDecimalString: pricingInfo.floatingPointDecimalString,
    priceCurrencyCode: pricingInfo.currency,
    formatForm: 'short',
    formatPrecision: 0,
    shouldRoundPriceUp: false,
  });
};

export const useStandardHotelRoomPriceMapping = () => {
  const { formatCurrency } = useCurrencyFormatter();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();
  return useMemo(() => {
    const formatCurrencyValue = getCurrencyFormatter(formatCurrency);
    return (pricingInfo: RawJoyHotelPricingInfo): StandardHotelPricingValues => {
      const shouldShowDiscount = (pricingInfo.strikeoutNightlyBaseRate?.valueInMinorUnits ?? 0) > (pricingInfo.nightlyBaseRate?.valueInMinorUnits ?? 0);
      return {
        discountPercentage: shouldShowDiscount
          ? getPercentageOff(
              minimumPercentageOffToDisplayValue,
              pricingInfo.nightlyBaseRate.valueInMinorUnits,
              pricingInfo.strikeoutNightlyBaseRate?.valueInMinorUnits,
              pricingInfo.nightlyBaseRate.digits,
              pricingInfo.strikeoutNightlyBaseRate?.digits,
            )
          : undefined,
        nightlyBaseRateString: formatCurrencyValue(pricingInfo.nightlyBaseRate),
        strikeoutNightlyBaseRateString: shouldShowDiscount && pricingInfo.strikeoutNightlyBaseRate ? formatCurrencyValue(pricingInfo.strikeoutNightlyBaseRate) : undefined,
        // strikeoutTotalRateString: pricingInfo.strikeoutTotalRate ? formatCurrencyValue(pricingInfo.strikeoutTotalRate) : undefined,
        totalRateString: formatCurrencyValue(pricingInfo.totalRate),
        nightlyBaseRate: Math.round(pricingInfo.nightlyBaseRate.valueInMinorUnits / Math.pow(10, pricingInfo?.nightlyBaseRate?.digits ?? 2)),
      };
    };
  }, [formatCurrency, minimumPercentageOffToDisplayValue]);
};

export type UseHotelRoomBlockPriceMappingReturn = ReturnType<typeof useHotelRoomBlockPriceMapping>;

export const useHotelRoomBlockPriceMapping = () => {
  const { formatCurrency } = useCurrencyFormatter();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();
  return useMemo(() => {
    const formatCurrencyValue = getCurrencyFormatter(formatCurrency);
    return (roomBlock: RoomBlockWithPricePerNight): HotelRoomBlockPricingValues => {
      const strikeoutPricePerNight = roomBlock.strikeoutPricePerNight;
      const shouldShowDiscount = (roomBlock.strikeoutPricePerNight ?? 0) > roomBlock.pricePerNight;
      return {
        discountPercentage: shouldShowDiscount
          ? getPercentageOff(minimumPercentageOffToDisplayValue, roomBlock.pricePerNight * 100, (strikeoutPricePerNight ?? 0) * 100, 2, 2)
          : undefined,
        nightlyBaseRateString: formatCurrencyValue({ floatingPointDecimalString: roomBlock.pricePerNight.toFixed(2), currency: roomBlock.currencyCode || 'USD' }),
        strikeoutNightlyBaseRateString:
          typeof strikeoutPricePerNight === 'number' && strikeoutPricePerNight > 0
            ? formatCurrencyValue({ floatingPointDecimalString: strikeoutPricePerNight.toFixed(2), currency: roomBlock.currencyCode || 'USD' })
            : undefined,
        nightlyBaseRate: roomBlock.pricePerNight,
      };
    };
  }, [minimumPercentageOffToDisplayValue, formatCurrency]);
};
